
import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { addDays, isFuture, isPast } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatDateBR } from "@/utils/dateUtils";

// Linha mínima que o card lê. A página /projetos já carrega a lista completa e
// pode passá-la via prop, evitando um 2º fetch da mesma tabela.
interface ProjetoVencimentoRow {
  id: string;
  status?: string | null;
  data_conclusao: string;
  freelancer?: { nome?: string | null } | null;
  agencia?: { nome?: string | null } | null;
}

interface FreelancersVencimentoCardProps {
  /** Projetos já carregados pelo pai (qualquer status). Se omitido, o card
   *  busca sozinho os "em_andamento". */
  projetos?: ProjetoVencimentoRow[];
}

const ProjetoCard = ({ projeto, onCopy }: { projeto: any; onCopy: (projeto: any) => void }) => (
  <div
    key={projeto.id}
    className="flex items-center justify-between p-3 bg-muted rounded-lg"
  >
    <div>
      <p className="font-medium">{projeto.freelancer?.nome}</p>
      <p className="text-sm text-muted-foreground">
        {projeto.agencia?.nome}
      </p>
      <p className="text-sm text-muted-foreground">
        Prazo: {formatDateBR(projeto.data_conclusao)}
      </p>
    </div>
    <Button
      variant="ghost"
      size="icon"
      onClick={() => onCopy(projeto)}
    >
      <Copy className="h-4 w-4" />
    </Button>
  </div>
);

export function FreelancersVencimentoCard({ projetos }: FreelancersVencimentoCardProps) {
  const { toast } = useToast();

  // Só busca quando o pai NÃO forneceu a lista (uso standalone). Na página
  // /projetos a lista já vem por prop, então esta query fica desabilitada.
  const { data: fetched } = useQuery({
    queryKey: ["projetosEmAndamento"],
    queryFn: async () => {
      const { data } = await supabase
        .from("projetos")
        .select(`
          *,
          freelancer:freelancers(nome),
          agencia:agencias(nome)
        `)
        .eq("status", "em_andamento");
      return (data ?? []) as ProjetoVencimentoRow[];
    },
    enabled: projetos === undefined,
  });

  const { vencidos, aVencer } = useMemo(() => {
    const source = (projetos ?? fetched ?? []).filter(
      (p) => p.status === "em_andamento"
    );
    const dataLimite = addDays(new Date(), 15);

    const vencidos = source
      .filter((projeto) => isPast(new Date(projeto.data_conclusao)))
      .sort((a, b) =>
        new Date(b.data_conclusao).getTime() - new Date(a.data_conclusao).getTime()
      );

    const aVencer = source
      .filter((projeto) =>
        isFuture(new Date(projeto.data_conclusao)) &&
        new Date(projeto.data_conclusao) <= dataLimite
      )
      .sort((a, b) =>
        new Date(a.data_conclusao).getTime() - new Date(b.data_conclusao).getTime()
      );

    return { vencidos, aVencer };
  }, [projetos, fetched]);

  const copiarParaWhatsapp = (projeto: any) => {
    const texto = `Freelancer: ${projeto.freelancer?.nome}\nAgência: ${
      projeto.agencia?.nome
    }\nPrazo: ${formatDateBR(projeto.data_conclusao)}`;
    navigator.clipboard.writeText(texto);
    toast({
      title: "Copiado!",
      description: "Texto copiado para a área de transferência",
    });
  };

  return (
    <div className="grid gap-6">
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-red-600">
            Freelancers com Prazo Vencido
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {vencidos.map((projeto) => (
              <ProjetoCard
                key={projeto.id}
                projeto={projeto}
                onCopy={copiarParaWhatsapp}
              />
            ))}
            {vencidos.length === 0 && (
              <p className="text-center text-muted-foreground">
                Nenhum freelancer com prazo vencido
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">
            Freelancers com Prazo Próximo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {aVencer.map((projeto) => (
              <ProjetoCard
                key={projeto.id}
                projeto={projeto}
                onCopy={copiarParaWhatsapp}
              />
            ))}
            {aVencer.length === 0 && (
              <p className="text-center text-muted-foreground">
                Nenhum freelancer com prazo próximo
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
